/** Turntable movie helpers (EarthImager manual 6.7): the azimuth schedule, MIME choice, pose rotation and the recorder. */

export const TURNTABLE_DURATIONS = [6, 12, 24];
export const TURNTABLE_FPS = 30;
// MP4 (H.264) where the browser records it, WebM otherwise
const MIME_CANDIDATES = ['video/mp4;codecs=avc1', 'video/mp4', 'video/webm;codecs=vp9', 'video/webm'];

/** File extension of a recording type: "mp4" or "webm". */
export const extensionFor = (mime) => (String(mime || '').startsWith('video/mp4') ? 'mp4' : 'webm');

/** Azimuth offsets (radians, 0 .. just under 2 pi) of each frame of a full turn lasting `durationS` at `fps`. */
export function turntableFrames(durationS, fps = TURNTABLE_FPS) {
  const n = Math.max(1, Math.round(durationS * fps));
  return Array.from({ length: n }, (_, i) => (2 * Math.PI * i) / n);
}

/** First supported recording type (mp4, then vp9 webm, then plain webm); null when MediaRecorder is missing. */
export function pickMime(MR = typeof MediaRecorder !== 'undefined' ? MediaRecorder : undefined) {
  if (!MR) return null;
  if (typeof MR.isTypeSupported !== 'function') return 'video/webm';
  return MIME_CANDIDATES.find((m) => MR.isTypeSupported(m)) || null;
}

/** Pose { position:[x,y,z], target:[x,y,z] } turned by `angle` about the vertical (y) axis through its target: zoom and elevation are kept. */
export function rotatePose(pose, angle) {
  const [px, py, pz] = pose.position;
  const [tx, ty, tz] = pose.target;
  const dx = px - tx, dz = pz - tz;
  const c = Math.cos(angle), s = Math.sin(angle);
  return { position: [tx + dx * c + dz * s, py, tz - dx * s + dz * c], target: [tx, ty, tz] };
}

/**
 * Records `canvas` while the camera orbits 360 degrees. `getPose`/`setPose` read and write the orbit pose;
 * the original pose is restored at the end. Resolves { blob, mime }; rejects with an AbortError when `signal` aborts.
 */
export function recordTurntable({
  canvas, getPose, setPose, durationS = 12, fps = TURNTABLE_FPS, onProgress, signal,
  MediaRecorderCtor = typeof MediaRecorder !== 'undefined' ? MediaRecorder : undefined,
  raf = (typeof requestAnimationFrame !== 'undefined' ? requestAnimationFrame : (f) => setTimeout(() => f(Date.now()), 16)),
  now = () => Date.now(),
}) {
  return new Promise((resolve, reject) => {
    const mime = pickMime(MediaRecorderCtor);
    if (!mime || !canvas?.captureStream) { reject(new Error('Video recording is not supported in this browser')); return; }
    const base = getPose();
    if (!base) { reject(new Error('The camera is not ready')); return; }
    const frames = turntableFrames(durationS, fps);
    const stream = canvas.captureStream(fps);
    const chunks = [];
    const rec = new MediaRecorderCtor(stream, { mimeType: mime });
    let cancelled = false;
    rec.ondataavailable = (e) => { if (e.data && e.data.size) chunks.push(e.data); };
    rec.onstop = () => {
      stream.getTracks?.().forEach((t) => t.stop());
      setPose(base);
      if (cancelled) { const err = new Error('Recording cancelled'); err.name = 'AbortError'; reject(err); return; }
      resolve({ blob: new Blob(chunks, { type: mime }), mime });
    };
    rec.onerror = (e) => { cancelled = true; reject(e?.error || new Error('Recording failed')); };
    rec.start();
    const t0 = now();
    const tick = () => {
      if (signal?.aborted) { cancelled = true; if (rec.state !== 'inactive') rec.stop(); return; }
      const elapsed = (now() - t0) / 1000;
      if (elapsed >= durationS) { if (rec.state !== 'inactive') rec.stop(); return; }
      setPose(rotatePose(base, frames[Math.min(frames.length - 1, Math.floor(elapsed * fps))]));
      onProgress?.(elapsed, durationS);
      raf(tick);
    };
    raf(tick);
  });
}
